<template>
  <ConfirmDialog ref="confirm"/>

  <Card>
    <div class="cardTitle">
      {{ cardTitle ? $t(cardTitle) : cardTitle }} <span v-if="categoriesSelectedList.length > 0">({{ categoriesSelectedList.length }})</span>
    </div>
    <div class="cardSubTitle" v-if="showSubTitle">{{ $t('Указание категории поможет участникам найти вашу закупку. Выберите одну или несколько категорий') }}</div>
    <div class="searchCategories" v-if="isShowAllCategories">
      <v-text-field
        :label="$t('Поиск')"
        prepend-inner-icon="mdi-magnify"
        v-model="searchText"
        density="compact"
        variant="solo"
        hide-details
        single-line
        @keyup.enter="searchCategories"
      />
      <v-btn
        prepend-icon="mdi-magnify"
        color="primary"
        @click="searchCategories"
      >{{ $t('Найти') }}</v-btn>
    </div>
    <v-list
      v-model:selected="categoriesSelectedList"
      select-strategy="leaf"
      class="categoriesList"
      :class="{'categoriesListOverflow': isShowAllCategories}"
    >
      <v-list-item
        v-for="category in displayedCategories"
        :title="category.name"
        :value="category.id"
      >
        <template v-slot:prepend>
          <v-checkbox-btn
            :model-value="categoriesSelectedList.includes(category.id)"
            @update:model-value="toggleCategory(category.id, $event)"
            true-icon="custom:checkboxOutline"
            false-icon="custom:checkboxBlankOutline"
            color="primary"
          />
        </template>
      </v-list-item>
    </v-list>
    <div class="categoriesListActions">
      <v-btn
        v-if="showMoreButton"
        variant="plain"
        class="categoriesListBtnShowAll"
        @click="isShowAllCategories = true"
      >{{ $t('Показать ещё') }}</v-btn>
      <v-btn
        v-if="isShowAllCategories"
        prepend-icon="mdi-check" 
        flat
        color="primary"
        density="comfortable"
        variant="outlined"
        @click="selectedAllCategories"
      >{{ $t('Выбрать все') }}</v-btn>
      <v-btn
        v-if="isShowAllCategories"
        flat
        color="primary"
        density="comfortable"
        variant="outlined"
        @click="resetAllCategories"
      >{{ $t('Сбросить все') }}</v-btn>
    </div>
  </Card>
</template>

<script>
import api from '@/api';
import { toast } from '@/lib/toast';
import Card from '@intec/vue-components/form/Card.vue';
import ConfirmDialog from "@intec/vue-components/form/ConfirmDialog.vue";

export default {
  name: 'CategoriesSelect',
  components: { Card, ConfirmDialog },
  props: {
    cardTitle: {
      type: String
    },
    categories: {
      type: Object,
      required: true
    },
    categoriesSelected: {
      type: Object,
      required: true
    },
    showSubTitle: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      searchText: null,
      categoriesSelectedList: this.categoriesSelected.map(c => c.id) ?? [],
      isShowAllCategories: false
    }
  },
  computed: {
    displayedCategories() {
      if (this.isShowAllCategories) {
        return this.categories.entries;
      }
      return this.categories.entries.slice(0, 5);
    },
    showMoreButton() {
      return this.categories.entries.length > 5 && !this.isShowAllCategories;
    }
  },
  watch: {
    categoriesSelectedList(newVal) {
      const selectedObjects = this.categories.entries.filter(c => newVal.includes(c.id));
      this.$emit('update:categoriesSelectedList', selectedObjects);
    }
  },
  methods: {
    toggleCategory(categoryId, isSelected) {
      if (isSelected) {
        this.categoriesSelectedList = [...this.categoriesSelectedList, categoryId];
      } else {
        this.categoriesSelectedList = this.categoriesSelectedList.filter(id => id !== categoryId);
      }
    },
    async searchCategories() {
      try {
        const categoriesAll = await api.get('/vocab/categories', {
          query: JSON.stringify({ 
            search: this.searchText
          })
        });
        this.categories.entries = categoriesAll.entries;
      } catch (e) {
        toast.error(e.message);
      }
    },
    selectedAllCategories() {
      this.categoriesSelectedList = this.categories.entries.map(c => c.id);
    },
    async resetAllCategories() {
      const resultDialog = await this.$refs.confirm.open(
        'Сбросить выбор',
        'Вы действительно хотите сбросить выбор по всем позициям?'
      );
      if (resultDialog) {
        this.categoriesSelectedList = [];
      } 
    }
  }
}
</script>

<style scoped lang="scss">
.card {
  gap: 12px;

  &SubTitle {
    font-size: 10px;
    font-weight: 500;
    color: rgb(var(--grey-1));
  }
}

/* Поисковая строка категорий */
.searchCategories {
  max-height: 45px;
  display: flex;
  flex-direction: row;
  gap: 15px;
}

:deep(.v-input__control) {
  border: 1px solid rgb(var(--border-color));
  border-radius: 8px;
}

:deep(.v-field--variant-solo, .v-field--variant-solo-filled) {
  box-shadow: none !important;
}

/* Список категорий */
.categoriesList {
  overflow-y: hidden;
  padding: 0;
  
  &Overflow {
    overflow-y: auto;
  }

  &Actions {
    display: flex;
    flex-direction: row;
    gap: 10px;
  }

  /* Кнопка "Показать ещё" */
  &BtnShowAll {
    height: fit-content;
    font-family: 'Manrope';
    font-size: 18px;
    font-weight: 600;
    color: rgb(var(--grey-1));
    padding: 0;
  }
}
:deep(.categoriesList .v-list-item) {
  padding: 0 !important;
}

:deep(.categoriesList .v-list-item > .v-list-item__content > .v-list-item-title) {
  white-space: pre-wrap;
  color: rgb(var(--black-color));
}

:deep(.categoriesList .v-list-item--active > .v-list-item__overlay) {
  opacity: 0 !important;
}
</style>
